// Typed API client for Kubdee Platform
// Uses fetch with httpOnly cookie auth (credentials: 'include')

export function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "staging.kubdee.com") return "https://api-staging.kubdee.com/api/v1";
    if (host === "kubdee.com") return "https://api.kubdee.com/api/v1";
    if (host === "localhost" || host === "127.0.0.1") {
      return import.meta.env.VITE_API_URL ?? "http://localhost:8787/api/v1";
    }
    console.warn(`[api-client] Unknown hostname "${host}", falling back to VITE_API_URL`);
  }
  if (!import.meta.env.VITE_API_URL && typeof window !== "undefined") {
    console.error("[api-client] VITE_API_URL is not set and hostname is not recognized. API calls may fail.");
  }
  return import.meta.env.VITE_API_URL ?? "http://localhost:8787/api/v1";
}

// Lazy — called at request time so hostname is available in browser
function BASE_URL() { return getBaseUrl(); }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

export interface ApiPaginatedResponse<T> {
  success: true;
  data: T[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages?: number;
  };
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export interface ApiErrorResponse {
  success: false;
  error: ApiErrorBody;
}

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.code = body.code;
    this.status = status;
    this.details = body.details;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(`${BASE_URL()}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;

      if (Array.isArray(value)) {
        for (const v of value) {
          url.searchParams.append(key, String(v));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return url.toString();
}

async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    params?: Record<string, unknown>;
    headers?: Record<string, string>;
  },
): Promise<T> {
  const url = buildUrl(path, options?.params);

  const isFormData = options?.body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options?.headers,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: options?.body
      ? isFormData
        ? (options.body as FormData)
        : JSON.stringify(options.body)
      : undefined,
  });

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  let json: ApiSuccessResponse<T> | ApiPaginatedResponse<T> | ApiErrorResponse;

  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, {
      code: "PARSE_ERROR",
      message: "Failed to parse API response",
    });
  }

  if (!json.success) {
    const errBody = (json as ApiErrorResponse).error ?? {
      code: "UNKNOWN_ERROR",
      message: "An unknown error occurred",
    };

    // User deactivated in tenant → redirect to login
    if (errBody.code === "USER_DEACTIVATED" && typeof window !== "undefined") {
      window.location.href = "/login?error=deactivated";
      throw new ApiError(res.status, errBody);
    }

    throw new ApiError(res.status, errBody);
  }

  // Return the full json so callers can access both `data` and `meta`
  return json as T;
}

// ---------------------------------------------------------------------------
// Public API client
// ---------------------------------------------------------------------------

export interface RequestOptions {
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, unknown>, options?: { headers?: Record<string, string> }) {
    return request<T>("GET", path, { params, headers: options?.headers });
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("POST", path, { body, params: options?.params, headers: options?.headers });
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("PUT", path, { body, params: options?.params, headers: options?.headers });
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions) {
    return request<T>("PATCH", path, { body, params: options?.params, headers: options?.headers });
  },

  delete<T>(
    path: string,
    params?: Record<string, unknown>,
    options?: { headers?: Record<string, string>; body?: unknown },
  ) {
    return request<T>("DELETE", path, {
      params,
      body: options?.body,
      headers: options?.headers,
    });
  },

  /**
   * Upload files via FormData.
   * Pass a FormData instance as the body — Content-Type will be set
   * automatically by the browser (with boundary).
   */
  upload<T>(path: string, formData: FormData, options?: RequestOptions) {
    return request<T>("POST", path, { body: formData, params: options?.params, headers: options?.headers });
  },
};

// ---------------------------------------------------------------------------
// File URL helper — resolve relative API paths to full URLs
// ---------------------------------------------------------------------------

/**
 * Resolve a file URL from API response.
 * If the URL is relative (starts with /), prepend the API origin.
 * If already absolute, return as-is.
 */
export function resolveFileUrl(url: string | null | undefined): string {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const origin = BASE_URL().replace(/\/api\/v1$/, "");
  return `${origin}${url}`;
}

// ---------------------------------------------------------------------------
// Files Worker URL helper
// ---------------------------------------------------------------------------

/**
 * Get the files worker URL for serving files from R2.
 * Public images use direct CDN cache, private images use signed tokens.
 */
export function getFilesUrl(): string {
  const env = import.meta.env.VITE_FILES_URL;
  if (env) return env;
  // Fallback based on API URL
  const apiUrl = getBaseUrl();
  if (apiUrl.includes("staging")) return "https://files-staging.kubdee.com";
  if (apiUrl.includes("localhost")) return "http://localhost:8790";
  return "https://files.kubdee.com";
}

// Convenience re-exports for typing responses in hooks
export type { ApiSuccessResponse as SuccessResponse, ApiPaginatedResponse as PaginatedResponse };
